import React, { useState, useRef, useEffect } from 'react';
import { useNotifications } from '../contexts/NotificationContext';
// Using Lucide icons or similar if available, otherwise using SVG
import { Bell } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const NotificationBell = () => {
	const { notifications, unreadCount, markAsRead, markAllAsRead, fetchNotifications } = useNotifications();
	const [isOpen, setIsOpen] = useState(false);
	const dropdownRef = useRef(null);
	const navigate = useNavigate();

	// Close on click outside
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				setIsOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	const toggleDropdown = () => {
		setIsOpen((prev) => {
			const next = !prev;
			if (next) {
				fetchNotifications?.();
			}
			return next;
		});
	};

	const handleMarkAsRead = (e, id) => {
		e.stopPropagation();
		markAsRead(id);
	};

	const handleMarkAllAsRead = () => {
		markAllAsRead();
		setIsOpen(false);
	};

	// Format date helper
	const formatDate = (dateString) => {
		const date = new Date(dateString);
		return new Intl.DateTimeFormat('es-DO', {
			month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
		}).format(date);
	};

	return (
		<div className="relative" ref={dropdownRef}>
			<button
				onClick={toggleDropdown}
				className="relative p-2 text-gray-600 hover:text-blue-600 transition-colors focus:outline-none"
				aria-label="Notificaciones"
			>
				<Bell size={24} />
				{unreadCount > 0 && (
					<span className="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform translate-x-1/4 -translate-y-1/4 bg-red-600 rounded-full">
						{unreadCount > 99 ? '99+' : unreadCount}
					</span>
				)}
			</button>

			{isOpen && (
				<div className="absolute right-0 z-50 mt-2 w-80 sm:w-96 bg-white rounded-lg shadow-xl border border-gray-100 overflow-hidden origin-top-right">
					<div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-100">
						<h3 className="text-sm font-semibold text-gray-700">Notificaciones</h3>
						{unreadCount > 0 && (
							<button
								onClick={handleMarkAllAsRead}
								className="text-xs text-blue-600 hover:text-blue-800 font-medium"
							>
								Marcar todas leídas
							</button>
						)}
					</div>

					<div className="max-h-96 overflow-y-auto">
						{notifications.length === 0 ? (
							<div className="px-4 py-6 text-center text-gray-500 text-sm">
								No tienes notificaciones
							</div>
						) : (
							<ul className="divide-y divide-gray-100">
								{notifications.map((notification) => (
									<li
										key={notification.id}
										className={`px-4 py-3 hover:bg-gray-50 transition-colors ${!notification.isRead ? 'bg-blue-50/50' : ''}`}
									>
										<div className="flex justify-between items-start gap-3">
											<div className="flex-1 min-w-0">
												<p className={`text-sm font-medium ${!notification.isRead ? 'text-gray-900' : 'text-gray-600'}`}>
													{notification.title}
												</p>
												<p className="text-sm text-gray-500 mt-1 line-clamp-2">
													{notification.message}
												</p>
												<p className="text-xs text-gray-400 mt-1">
													{formatDate(notification.createdAt)}
												</p>
											</div>
											{!notification.isRead && (
												<button
													onClick={(e) => handleMarkAsRead(e, notification.id)}
													className="text-gray-400 hover:text-blue-600 p-1 rounded-full hover:bg-blue-50 transition-colors"
													title="Marcar como leída"
												>
													<span className="w-2 h-2 bg-blue-600 rounded-full block"></span>
												</button>
											)}
										</div>
									</li>
								))}
							</ul>
						)}
					</div>

					<div className="px-4 py-2 bg-gray-50 border-t border-gray-100 text-center space-y-1">
						<button
							className="text-xs text-gray-500 hover:text-gray-700 font-medium block w-full"
							onClick={() => {
								setIsOpen(false);
								navigate('/settings/notifications');
							}}
						>
							Mis preferencias
						</button>
						<button
							className="text-xs text-gray-500 hover:text-gray-700 font-medium block w-full"
							onClick={() => {
								setIsOpen(false);
								navigate('/notifications');
							}}
						>
							Ver historial completo
						</button>
					</div>
				</div>
			)}
		</div>
	);
};

export default NotificationBell;
